import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Save, Mail, Send, Plus, X, Eye, EyeOff } from "lucide-react";

export default function EmailSettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const [initial, setInitial] = useState(null);
    const [cfg, setCfg] = useState({
        smtp_host: "smtp.gmail.com",
        smtp_port: 465,
        smtp_user: "",
        smtp_password: "",
        sender_name: "福缘珠宝 Fuyuan Jewelry",
        from_name: "FuYuan Jewelry",
        from_email: "",
        reply_to: "",
        internal_recipients: [],
        notify_on_created: true,
        notify_on_paid: true,
        customer_email_enabled: true,
        subject_created: "",
        subject_paid_internal: "",
        subject_customer: "",
        customer_greeting: "",
        email_sign_off: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/email-settings");
            const next = {
                smtp_host: data.smtp_host || "smtp.gmail.com",
                smtp_port: data.smtp_port || 465,
                smtp_user: data.smtp_user || "",
                smtp_password: data.smtp_password_masked || "",
                sender_name: data.sender_name || "福缘珠宝 Fuyuan Jewelry",
                from_name: data.from_name || "FuYuan Jewelry",
                from_email: data.from_email || "",
                reply_to: data.reply_to || "",
                internal_recipients: Array.isArray(data.internal_recipients) ? data.internal_recipients : [],
                notify_on_created: !!data.notify_on_created,
                notify_on_paid: !!data.notify_on_paid,
                customer_email_enabled: !!data.customer_email_enabled,
                subject_created: data.subject_created || "",
                subject_paid_internal: data.subject_paid_internal || "",
                subject_customer: data.subject_customer || "",
                customer_greeting: data.customer_greeting || "",
                email_sign_off: data.email_sign_off || "",
            };
            setCfg(next);
            setInitial(next);
        } catch (e) {
            toast.error(e.response?.data?.detail || "读取失败");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const dirty = initial && JSON.stringify({ ...cfg, smtp_password: cfg.smtp_password.includes("····") ? "" : cfg.smtp_password }) !==
                              JSON.stringify({ ...initial, smtp_password: initial.smtp_password.includes("····") ? "" : initial.smtp_password });

    const update = (patch) => setCfg((s) => ({ ...s, ...patch }));
    const addRecipient = () => update({ internal_recipients: [...cfg.internal_recipients, ""] });
    const setRecipient = (i, v) => update({ internal_recipients: cfg.internal_recipients.map((x, idx) => idx === i ? v : x) });
    const removeRecipient = (i) => update({ internal_recipients: cfg.internal_recipients.filter((_, idx) => idx !== i) });

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                smtp_host: cfg.smtp_host,
                smtp_port: Number(cfg.smtp_port),
                smtp_user: cfg.smtp_user,
                sender_name: cfg.sender_name,
                from_name: cfg.from_name,
                from_email: cfg.from_email,
                reply_to: cfg.reply_to,
                internal_recipients: cfg.internal_recipients.filter((r) => r && r.trim()),
                notify_on_created: cfg.notify_on_created,
                notify_on_paid: cfg.notify_on_paid,
                customer_email_enabled: cfg.customer_email_enabled,
                subject_created: cfg.subject_created,
                subject_paid_internal: cfg.subject_paid_internal,
                subject_customer: cfg.subject_customer,
                customer_greeting: cfg.customer_greeting,
                email_sign_off: cfg.email_sign_off,
            };
            if (cfg.smtp_password && !cfg.smtp_password.includes("····")) body.smtp_password = cfg.smtp_password;
            await api.put("/admin/email-settings", body);
            toast.success("邮件设置已保存");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "保存失败");
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/admin/notifications/test", {});
            toast.success(data.message || "测试邮件已发送");
        } catch (e) {
            toast.error(e.response?.data?.detail || "测试失败", { duration: 8000 });
        } finally {
            setTesting(false);
        }
    };

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewKind, setPreviewKind] = useState("created");
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);

    const loadPreview = async (kind) => {
        setPreviewKind(kind);
        setPreviewLoading(true);
        try {
            const { data } = await api.get(`/admin/notifications/preview?kind=${kind}`);
            setPreviewHtml(data.html || "");
        } catch (e) {
            toast.error(e.response?.data?.detail || "预览加载失败");
        } finally {
            setPreviewLoading(false);
        }
    };

    const openPreview = () => { setPreviewOpen(true); loadPreview("created"); };

    return (
        <div className="space-y-6" data-testid="email-settings-page">
            <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">系统设置</div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1 flex items-center gap-3">
                    <Mail className="h-7 w-7 text-gold-600" /> 邮件通知
                </h1>
                <p className="text-sm text-gold-500 mt-1">
                    下单与付款事件自动发邮件；密码只写不读回明文
                </p>
            </div>

            <Card className="p-6 border-slate-200 shadow-sm max-w-3xl" data-testid="email-settings-card">
                {loading ? (
                    <div className="text-sm text-gold-500">加载中...</div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-3">
                            <div className="space-y-1.5">
                                <Label>SMTP 主机</Label>
                                <Input data-testid="email-smtp-host" value={cfg.smtp_host} onChange={(e) => update({ smtp_host: e.target.value })} placeholder="smtp.gmail.com" className="font-mono text-xs" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>端口</Label>
                                <Input data-testid="email-smtp-port" type="number" value={cfg.smtp_port} onChange={(e) => update({ smtp_port: e.target.value })} className="font-mono text-xs" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>发件账号 (SMTP User)</Label>
                            <Input data-testid="email-smtp-user" type="email" value={cfg.smtp_user} onChange={(e) => update({ smtp_user: e.target.value })} placeholder="candy@fuyuanjewelry.my" className="font-mono text-xs" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>应用专用密码</Label>
                            <div className="relative">
                                <Input
                                    data-testid="email-smtp-password"
                                    type={showPw ? "text" : "password"}
                                    value={cfg.smtp_password}
                                    onChange={(e) => update({ smtp_password: e.target.value })}
                                    placeholder="Gmail 应用专用密码（16 字符）"
                                    className="font-mono text-xs pr-10"
                                />
                                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <div className="text-xs text-gold-500">Gmail：账号 → 安全性 → 开启两步验证 → 应用专用密码 → 生成 16 字符密码粘贴至此。留空则保留旧密码。</div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>显示名（发件人）</Label>
                            <Input data-testid="email-sender-name" value={cfg.sender_name} onChange={(e) => update({ sender_name: e.target.value })} placeholder="福缘珠宝 Fuyuan Jewelry" />
                        </div>

                        <div className="pt-4 border-t border-slate-200 space-y-4">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-gold-600">显示发件人（对收件人可见）</div>
                                <p className="text-xs text-gold-500 mt-1">和 SMTP 登录账号分开：登录用 smtp_user 认证，邮件头显示这里配置的名称与邮箱。</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>From Name（显示名）</Label>
                                    <Input data-testid="email-from-name" value={cfg.from_name} onChange={(e) => update({ from_name: e.target.value })} placeholder="FuYuan Jewelry" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>From Email（显示邮箱）</Label>
                                    <Input data-testid="email-from-email" type="email" value={cfg.from_email} onChange={(e) => update({ from_email: e.target.value })} placeholder="orders@fuyuanjewelry.my" className="font-mono text-xs" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Reply-To（客户回信到这里，留空则同 From Email）</Label>
                                <Input data-testid="email-reply-to" type="email" value={cfg.reply_to} onChange={(e) => update({ reply_to: e.target.value })} placeholder="留空则自动使用 From Email" className="font-mono text-xs" />
                            </div>
                            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 leading-relaxed">
                                <b>⚠️ Gmail 提醒</b>：如果 SMTP 登录账号（如 candy@fuyuanjewelry.my）与 From Email（如 orders@fuyuanjewelry.my）不同，Gmail 默认会把 From 强制改写回登录账号。请先在 Gmail 里
                                <a href="https://mail.google.com/mail/u/0/#settings/accounts" target="_blank" rel="noopener noreferrer" className="underline mx-1">「设置 → 账号 → 用这个地址发送邮件」</a>
                                添加 orders@fuyuanjewelry.my 为已验证别名，Gmail 才会保留您设置的 From Email。
                            </div>
                        </div>

                        <div className="space-y-2 pt-2 border-t border-slate-200">
                            <div className="flex items-center justify-between">
                                <Label>内部收件人</Label>
                                <Button type="button" variant="outline" size="sm" onClick={addRecipient} data-testid="email-add-recipient" className="h-7 text-xs gap-1">
                                    <Plus className="h-3 w-3" /> 加一个
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {cfg.internal_recipients.map((r, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <Input data-testid={`email-recipient-${i}`} type="email" value={r} onChange={(e) => setRecipient(i, e.target.value)} placeholder="candy@fuyuanjewelry.my" className="flex-1 font-mono text-xs" />
                                        <Button type="button" variant="ghost" size="sm" onClick={() => removeRecipient(i)} className="h-9 w-9 p-0 text-red-500 hover:bg-red-50" title="删除">
                                            <X className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                ))}
                                {cfg.internal_recipients.length === 0 && (
                                    <div className="text-xs text-slate-400 italic">暂无收件人，点右上「加一个」开始</div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                            <label className="flex items-center gap-2 p-3 rounded-md border border-slate-200 bg-gold-50 cursor-pointer">
                                <Switch data-testid="email-notify-created" checked={cfg.notify_on_created} onCheckedChange={(v) => update({ notify_on_created: v })} />
                                <span className="text-sm text-slate-900">下单时通知内部</span>
                            </label>
                            <label className="flex items-center gap-2 p-3 rounded-md border border-slate-200 bg-gold-50 cursor-pointer">
                                <Switch data-testid="email-notify-paid" checked={cfg.notify_on_paid} onCheckedChange={(v) => update({ notify_on_paid: v })} />
                                <span className="text-sm text-slate-900">付款时通知内部</span>
                            </label>
                            <label className="flex items-center gap-2 p-3 rounded-md border border-slate-200 bg-gold-50 cursor-pointer">
                                <Switch data-testid="email-notify-customer" checked={cfg.customer_email_enabled} onCheckedChange={(v) => update({ customer_email_enabled: v })} />
                                <span className="text-sm text-slate-900">发确认信给顾客</span>
                            </label>
                        </div>

                        <div className="pt-4 border-t border-slate-200 space-y-4">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-gold-600">邮件文案</div>
                                <p className="text-xs text-gold-500 mt-1">
                                    主题行支持占位符 <code className="bg-gold-50 px-1 rounded">{"{invoice_no}"}</code> 和 <code className="bg-gold-50 px-1 rounded">{"{total}"}</code>，会被自动替换成订单编号与合计金额。
                                </p>
                            </div>
                            <div className="space-y-1.5">
                                <Label>① 下单待付款 · 内部通知 主题</Label>
                                <Input
                                    data-testid="email-subject-created"
                                    value={cfg.subject_created}
                                    onChange={(e) => update({ subject_created: e.target.value })}
                                    placeholder="【新订单 · 待付款】{invoice_no} · {total}"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>② 已付款 · 内部通知 主题</Label>
                                <Input
                                    data-testid="email-subject-paid-internal"
                                    value={cfg.subject_paid_internal}
                                    onChange={(e) => update({ subject_paid_internal: e.target.value })}
                                    placeholder="【已付款】{invoice_no} · {total}"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>③ 订单确认 · 顾客邮件 主题</Label>
                                <Input
                                    data-testid="email-subject-customer"
                                    value={cfg.subject_customer}
                                    onChange={(e) => update({ subject_customer: e.target.value })}
                                    placeholder="订单确认 · {invoice_no} · 福缘珠宝"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>顾客邮件 · 开头问候语</Label>
                                <Textarea
                                    data-testid="email-customer-greeting"
                                    rows={2}
                                    value={cfg.customer_greeting}
                                    onChange={(e) => update({ customer_greeting: e.target.value })}
                                    placeholder="感谢您的订购，我们已收到您的付款，正在为您悉心准备。"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>邮件底部 · 结尾祝福语</Label>
                                <Input
                                    data-testid="email-sign-off"
                                    value={cfg.email_sign_off}
                                    onChange={(e) => update({ email_sign_off: e.target.value })}
                                    placeholder="福缘珠宝 Fuyuan Jewelry 敬上"
                                />
                                <div className="text-xs text-gold-500">仅显示在顾客确认邮件底部（内部通知不显示）。</div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                            <Button data-testid="save-email-btn" onClick={save} disabled={saving || !dirty} className="bg-gold-600 hover:bg-gold-700 text-white gap-2">
                                <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存"}
                            </Button>
                            <Button data-testid="test-email-btn" variant="outline" onClick={sendTest} disabled={testing} className="gap-2">
                                <Send className="h-4 w-4" /> {testing ? "发送中..." : "发送测试邮件"}
                            </Button>
                            <Button data-testid="preview-email-btn" variant="outline" onClick={openPreview} className="gap-2">
                                <Eye className="h-4 w-4" /> 预览邮件
                            </Button>
                            {dirty && <span className="text-xs text-amber-600">有未保存的修改</span>}
                        </div>
                    </div>
                )}

                {previewOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" data-testid="email-preview-modal">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setPreviewOpen(false)} />
                        <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden">
                            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200 shrink-0">
                                <div className="flex items-center gap-2">
                                    <Eye className="h-4 w-4 text-gold-600" />
                                    <div className="text-lg font-semibold">邮件预览</div>
                                    <span className="text-xs text-gold-500 ml-2">使用示例订单渲染，非真实发送</span>
                                </div>
                                <button data-testid="close-preview-btn" onClick={() => setPreviewOpen(false)} className="text-slate-400 hover:text-slate-700">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 bg-gold-50/40 shrink-0 flex-wrap">
                                {[
                                    { k: "created", label: "① 下单待付款 · 内部" },
                                    { k: "paid_internal", label: "② 已付款 · 内部" },
                                    { k: "paid_customer", label: "③ 订单确认 · 顾客" },
                                ].map((t) => (
                                    <button
                                        key={t.k}
                                        data-testid={`preview-tab-${t.k}`}
                                        onClick={() => loadPreview(t.k)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                            previewKind === t.k
                                                ? "bg-gold-600 text-white border-gold-600"
                                                : "bg-white text-slate-600 border-slate-300 hover:border-gold-400"
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                            <div className="flex-1 overflow-hidden bg-slate-100 p-4 min-h-[420px]">
                                {previewLoading ? (
                                    <div className="w-full h-full flex items-center justify-center text-sm text-gold-500">加载中...</div>
                                ) : (
                                    <iframe
                                        data-testid="email-preview-iframe"
                                        srcDoc={previewHtml}
                                        title="邮件预览"
                                        sandbox=""
                                        className="w-full h-full bg-white rounded-md shadow-sm border border-slate-200"
                                        style={{ minHeight: 400 }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
